import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from '../i18n/useTranslation';

const DateRangePicker = ({ startDate, endDate, onStartDateChange, onEndDateChange, min, className = '' }) => {
  const { language } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(() => {
    if (startDate) {
      const date = new Date(startDate);
      return new Date(date.getFullYear(), date.getMonth(), 1);
    }
    return new Date(new Date().getFullYear(), new Date().getMonth(), 1);
  });
  const pickerRef = useRef(null);
  const isRTL = language === 'ar';

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const getDaysInMonth = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }
    return days;
  };

  const isToday = (date) => {
    if (!date) return false;
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  };

  const isInRange = (date) => {
    if (!date || !startDate || !endDate) return false;
    const checkDate = new Date(date);
    const start = new Date(startDate);
    const end = new Date(endDate);
    checkDate.setHours(0, 0, 0, 0);
    start.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);
    return checkDate >= start && checkDate <= end;
  };

  const isStartDate = (date) => {
    if (!date || !startDate) return false;
    const checkDate = new Date(date);
    const start = new Date(startDate);
    checkDate.setHours(0, 0, 0, 0);
    start.setHours(0, 0, 0, 0);
    return checkDate.getTime() === start.getTime();
  };

  const isEndDate = (date) => {
    if (!date || !endDate) return false;
    const checkDate = new Date(date);
    const end = new Date(endDate);
    checkDate.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);
    return checkDate.getTime() === end.getTime();
  };

  const isPast = (date) => {
    if (!date || !min) return false;
    const minDate = new Date(min);
    const checkDate = new Date(date);
    minDate.setHours(0, 0, 0, 0);
    checkDate.setHours(0, 0, 0, 0);
    return checkDate < minDate;
  };

  const handleDateClick = (date) => {
    if (!date || isPast(date)) return;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;

    if (!startDate || (startDate && endDate)) {
      // Start new selection
      onStartDateChange(dateStr);
      onEndDateChange('');
    } else if (startDate && !endDate) {
      // Complete the range
      const start = new Date(startDate);
      const clicked = new Date(dateStr);
      if (clicked < start) {
        // If clicked date is before start, swap them
        onEndDateChange(startDate);
        onStartDateChange(dateStr);
      } else {
        onEndDateChange(dateStr);
      }
    }
  };

  const goToPreviousMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const monthNames = language === 'ar'
    ? ['يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو', 'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر']
    : ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

  const dayNames = language === 'ar'
    ? ['أحد', 'إثنين', 'ثلاثاء', 'أربعاء', 'خميس', 'جمعة', 'سبت']
    : ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const formatDisplayDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString + 'T00:00:00');
    // Always use Gregorian calendar, but with Arabic month names if Arabic is selected
    const monthNames = language === 'ar'
      ? ['يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو', 'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر']
      : ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    const dayNames = language === 'ar'
      ? ['الأحد', 'الإثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت']
      : ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    
    const day = dayNames[date.getDay()];
    const month = monthNames[date.getMonth()];
    const dayNum = date.getDate();
    const year = date.getFullYear();
    
    return language === 'ar' 
      ? `${day}، ${dayNum} ${month} ${year}`
      : `${day}, ${month} ${dayNum}, ${year}`;
  };

  const days = getDaysInMonth();
  const displayText = startDate && endDate 
    ? `${formatDisplayDate(startDate)} - ${formatDisplayDate(endDate)}`
    : startDate 
    ? `${formatDisplayDate(startDate)} - ...`
    : 'Select date range';

  return (
    <div className="relative" ref={pickerRef} dir={isRTL ? 'rtl' : 'ltr'}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-left bg-white flex items-center justify-between ${className}`}
      >
        <span className={startDate ? 'text-gray-900' : 'text-gray-500'}>
          {displayText}
        </span>
        <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute top-full mt-2 bg-white rounded-xl shadow-2xl border-2 border-gray-200 p-4 z-50 min-w-[300px]">
          <div className={`flex items-center justify-between mb-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <button
              onClick={goToPreviousMonth}
              className="p-2 hover:bg-gray-100 rounded-lg transition"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={isRTL ? "M9 5l7 7-7 7" : "M15 19l-7-7 7-7"} />
              </svg>
            </button>
            <h3 className="text-lg font-semibold text-gray-900">
              {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
            </h3>
            <button
              onClick={goToNextMonth}
              className="p-2 hover:bg-gray-100 rounded-lg transition"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={isRTL ? "M15 19l-7-7 7-7" : "M9 5l7 7-7 7"} />
              </svg>
            </button>
          </div>

          <div className="grid grid-cols-7 gap-1 mb-2">
            {dayNames.map((day) => (
              <div key={day} className="text-center text-xs font-semibold text-gray-600 py-2">
                {day}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-7 gap-1">
            {days.map((date, index) => {
              if (!date) {
                return <div key={`empty-${index}`} className="aspect-square" />;
              }

              const past = isPast(date);
              const today = isToday(date);
              const inRange = isInRange(date);
              const isStart = isStartDate(date);
              const isEnd = isEndDate(date);

              return (
                <button
                  key={date.toISOString()}
                  type="button"
                  onClick={() => handleDateClick(date)}
                  disabled={past}
                  className={`
                    aspect-square p-2 rounded-lg text-sm font-medium transition-all relative
                    ${past
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : isStart || isEnd
                      ? 'bg-blue-600 text-white shadow-lg scale-105 z-10'
                      : inRange
                      ? 'bg-blue-100 text-blue-700'
                      : today
                      ? 'bg-blue-50 text-blue-700 hover:bg-blue-100'
                      : 'bg-white text-gray-700 hover:bg-gray-100'
                    }
                  `}
                >
                  {date.getDate()}
                  {inRange && !isStart && !isEnd && (
                    <div className="absolute inset-0 bg-blue-100 -z-0" style={{ width: '100%' }}></div>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default DateRangePicker;

